import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import styled from "@emotion/styled";
import { Spinner } from "@ui-kit/feedback/Spinner/Spinner.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CartQuantityButtons from "@shared/components/application/form/CartQuantityButtons/CartQuantityButtons.web";

import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import { X_SP_UUID } from "@shared/env";
import RemoveAllSeatsModal from "@shared/modules/seating/components/RemoveAllSeatsModal/RemoveAllSeatsModal.web";
import { DISPLAY_QUANTITY_DROPDOWN_THRESHOLD, enums } from "@shared/constants";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";

import { NestedPublicVenueSerializer } from "@shared/modules/events/types/public";

import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { TicketBasketType } from "@shared/modules/basket/types";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { trackItemsInCart } from "@shared/modules/events/utils/detail-page-helpers";
import { getStartsOnDate } from "@shared/modules/events/utils/event-utils";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SEATING_ITEM_TYPE_MAP } from "@shared/modules/seating/types/seating-property-maps";
import { SeatingEventPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { getFormattedDate } from "@shared/utils/dates/get-dates/get-dates-timezone";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import useDebounce from "@shared/hooks/useDebounce";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import SeatRemoveModal from "@shared/modules/seating/components/SeatRemoveModal/SeatRemoveModal.web";
import UpgradeOffer from "@app-contexts/public/features/basket/components/cart-summary/cart-summary-items/UpgradeOffer/UpgradeOffer.web";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { isProductPackageParent } from "@shared/modules/basket/utils/helpers";
import ChildPackageCartItem from "../../ChildPackageCartItem/ChildPackageCartItem.web";
import AssignedSeatCartItem from "../../AssignedSeatCartItem/AssignedSeatCartItem.web";
import LocationCartItem from "../../LocationCartItem/LocationCartItem.web";
import CartQuantitySelect from "../CartQuantitySelect/CartQuantitySelect.web";
import { CartItemProps } from "./CartItem.props";

const notifier = NotifierFactory.createNotifier();

// TODO fix this
const ItemContainer = styled(Flex)`
  flex-direction: column;
  font-weight: bold;
  margin-bottom: 20px;
`;

const StyledIconButton = styled(IconButton)`
  color: var(--clr-paint-gray-500);
  height: 24px;
  width: 24px;

  :hover {
    background-color: transparent;
    color: var(--clr-paint-red-600);
  }
`;

const CartItem = ({
  item,
  venue,
  event = undefined,
  product = undefined,
  disabled = false,
  ticketType = null,
  productAttribute = null,
  membershipLevel = null,
  onCartClose = undefined,
  holdPurchaseLimits = undefined,
  itemGroupUpgrades,
}: CartItemProps) => {
  const {
    removeItems,
    modifyItems,
    itemGroups,
    upgradeItemGroup,
    isBasketUpdating,
  } = usePublicBasket();
  // Membership levels and item_groups with info_required differ in that they are split into 1 item_group per membership,
  const itemGroupsAreSplit =
    item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id ||
    item.info_required ||
    (item && isProductPackageParent(item, itemGroups));

  const getMinSelectionAmount = useCallback(() => {
    if (holdPurchaseLimits?.min) {
      return holdPurchaseLimits.min;
    }
    return !itemGroupsAreSplit ? ticketType?.minimum_purchase_limit ?? 0 : 0;
  }, [
    holdPurchaseLimits?.min,
    itemGroupsAreSplit,
    ticketType?.minimum_purchase_limit,
  ]);

  const minSelectionAmount = getMinSelectionAmount();
  const maxSelectionAmount =
    holdPurchaseLimits?.max ??
    ticketType?.purchase_limit ??
    productAttribute?.purchase_limit ??
    membershipLevel?.purchase_limit ??
    8;
  const { t } = useTranslation("purchases");

  const {
    actions: seatingActions,
    modalValues,
    seatingServiceState,
  } = useSeatingState();

  const {
    modalOpen,
    resolveModal,
    rejectModal,
    seatsToRemove,
    removeAllSeatsData,
  } = modalValues;

  const itemVenue: NestedPublicVenueSerializer =
    venue ?? (event?.venue as NestedPublicVenueSerializer);

  const analytics = useAnalytics();

  // This controls the quantity amount requested to the API, changed by this component only.
  const [amountRequested, setAmountRequested] = useState({
    amount: item.quantity,
  });
  const [debouncedAmountRequested] = useDebounce(
    amountRequested,
    !!maxSelectionAmount &&
      maxSelectionAmount - minSelectionAmount >
        DISPLAY_QUANTITY_DROPDOWN_THRESHOLD &&
      !itemGroupsAreSplit
      ? 0
      : 500
  );

  const hasLocation = item?.location_permissions?.some(
    (lperm) => lperm.location
  );
  const hasSeat = item?.permissions?.some((perm) => perm.seat);
  const isCustomPackage = item.is_custom_package;
  const hasSubItems = hasLocation || hasSeat || isCustomPackage;
  const isMembershipRenewal = item.renewal_member;

  // fetch full event data if event doesn't exist and the cart item has assigned seating
  // used for seat/location removal from the mobile cart
  const { useRetrieveWithMembershipTicketTypesOverride: useRetrieveEvent } =
    usePublicEventService();
  const { data: eventData } = useRetrieveEvent(item.event, {
    options: {
      enabled: !event && Boolean(item.event) && (hasLocation || hasSeat),
    },
  });

  // fetch full event data if event doesn't exist and the cart item has assigned seating
  // used for seat/location removal from the mobile cart
  const { useRetrieve: useRetrieveMembership } =
    usePublicMembershipGroupsService();
  const { data: membershipGroupData } = useRetrieveMembership(
    Number(item.membership_group),
    {
      options: {
        enabled: Boolean(item.membership_group) && (hasLocation || hasSeat),
      },
    }
  );

  // This controls the quantity amount displayed to the user, changed by this component and by the basket.
  const [amountSelectedDisplay, setAmountSelectedDisplay] = useState(
    item.quantity
  );

  useEffect(() => {
    // Change the displayed quantity when the basket quantity changes.
    setAmountSelectedDisplay(item.quantity ?? 0);
  }, [item.quantity]);

  useEffect(() => {
    // Change the displayed quantity when this component requests a change.
    setAmountSelectedDisplay(amountRequested.amount ?? 0);
  }, [amountRequested]);

  const didMount = useRef(false);
  const allowRequest = useRef(true);

  // Universal function for handling tracking of TicketTypes, MembershipTypes, ProductTypes.
  const trackSuccessfulQuantityRequest = useCallback(
    (basket: TicketBasketType) => {
      const spAddToCartCall = () => {
        UserInteractionLogger.addToCart({
          item,
          count: debouncedAmountRequested.amount ?? 0,
          venue: itemVenue?.name ?? "",
          basket,
          event,
          product,
          productVariant: productAttribute,
          membershipGroup: membershipGroupData,
        });
      };

      const itemId =
        ticketType?.id ?? membershipLevel?.id ?? productAttribute?.id;
      const itemVariant =
        ticketType?.name ?? membershipLevel?.name ?? productAttribute?.name;
      const itemName =
        event?.name ?? membershipGroupData?.name ?? product?.name;

      trackItemsInCart(
        debouncedAmountRequested.amount ?? 0,
        basket,
        {
          itemId,
          itemName,
          itemVariant,
          itemType: item.item_type,
        },
        itemVenue,
        analytics,
        spAddToCartCall,
        { uuid: basket[X_SP_UUID] }
      );
    },
    [
      analytics,
      debouncedAmountRequested.amount,
      event,
      item,
      itemVenue,
      membershipLevel,
      product,
      productAttribute,
      ticketType?.id,
      membershipGroupData?.name,
      ticketType?.name,
    ]
  );

  useEffect(() => {
    if (!didMount.current) {
      // do nothing on the first component render
      didMount.current = true;
      return;
    }

    if (
      allowRequest.current &&
      Number.isFinite(debouncedAmountRequested.amount)
    ) {
      modifyItems(
        {
          item,
          quantity: debouncedAmountRequested.amount ?? 0,
        },
        {}
      )
        .then(trackSuccessfulQuantityRequest)
        .catch((err) => {
          // On error, revert displayed amount to basket amount
          setAmountSelectedDisplay(item?.quantity ?? 0);
          allowRequest.current = false;
          setAmountRequested({ amount: item?.quantity ?? 0 });
          logAppInfo("Error changing cart item quantity");
          recordAppError(err);
        });
    } else {
      allowRequest.current = true;
    }
  }, [debouncedAmountRequested]); //eslint-disable-line

  const deleteItem = useCallback(() => {
    removeItems(item, {})
      .then(() => {
        analytics.removeFromCart();
      })
      .catch((err) => {
        // On error, revert displayed amount to basket amount
        setAmountSelectedDisplay(item?.quantity ?? 0);
        allowRequest.current = false;
        setAmountRequested({ amount: item?.quantity ?? 0 });
        logAppInfo("Error removing cart item");
        recordAppError(err);
      });
  }, [analytics, item, removeItems]);

  const add = useCallback(() => {
    let newQuantity = debouncedAmountRequested.amount ?? 1;
    // Membership levels and item_groups with info_required differ in that they are split into 1 item_group per membership
    // So adding means *all* item groups of the same type needs to be taken into account when adding
    if (itemGroupsAreSplit) {
      if (isProductPackageParent(item, itemGroups)) {
        const itemsInBasket = itemGroups.filter((ig) => ig.type === item.type);
        const currQuantity = itemsInBasket.length;
        newQuantity = currQuantity + 1;
      } else if (
        item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      ) {
        const membershipLevelsInBasket = itemGroups.filter(
          (ig) => ig.membership_level === item.membership_level
        );

        const membershipQuantity = item.info_required
          ? membershipLevelsInBasket.length
          : membershipLevelsInBasket[0].quantity;

        const currQuantity =
          item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
            ? membershipQuantity
            : amountRequested.amount ?? 0;
        newQuantity = currQuantity + 1;
      } else if (item.info_required) {
        const itemsInBasket = itemGroups.filter(
          (ig) =>
            ig.info_required &&
            ig.item_type === item.item_type &&
            ig.type === item.type
        );
        const currQuantity = itemsInBasket.length;
        newQuantity = currQuantity;
      }
    }

    modifyItems(
      {
        item,
        quantity: newQuantity,
      },
      {}
    )
      .then(trackSuccessfulQuantityRequest)
      .catch((err) => {
        // On error, revert displayed amount to basket amount
        setAmountSelectedDisplay(item?.quantity ?? 0);
        allowRequest.current = false;
        setAmountRequested({ amount: item?.quantity ?? 0 });
        logAppInfo("Error increasing cart item quantity");
        recordAppError(err);
      });
  }, [
    amountRequested.amount,
    debouncedAmountRequested.amount,
    item,
    itemGroups,
    itemGroupsAreSplit,
    modifyItems,
    trackSuccessfulQuantityRequest,
  ]);

  const remove = () => {
    let newQuantity = amountRequested.amount ?? 1;
    // Membership levels and item_groups with info_required differ in that they are split into 1 item_group per membership
    // So removing means *all* item groups of the same type needs to be taken into account when removing
    if (itemGroupsAreSplit) {
      if (isProductPackageParent(item, itemGroups)) {
        const itemsInBasket = itemGroups.filter((ig) => ig.type === item.type);
        const currQuantity = itemsInBasket.length;
        newQuantity = currQuantity - 1;
      } else if (
        item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      ) {
        const membershipLevelsInBasket = itemGroups.filter(
          (ig) => ig.membership_level === item.membership_level
        );

        const membershipQuantity = item.info_required
          ? membershipLevelsInBasket.length
          : membershipLevelsInBasket[0].quantity;

        const currQuantity =
          item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
            ? membershipQuantity
            : amountRequested.amount ?? 0;
        newQuantity = currQuantity - 1;
      } else if (item.info_required) {
        const itemsInBasket = itemGroups.filter(
          (ig) =>
            ig.info_required &&
            ig.item_type === item.item_type &&
            ig.type === item.type
        );
        const currQuantity = itemsInBasket.length;

        if (currQuantity <= ticketType?.minimum_purchase_limit ?? 0) {
          newQuantity = 0;
        } else {
          newQuantity = currQuantity;
        }
      }
    }
    // Perform modifyItems inside of this remove function here, as the basket's removeItems does not handle quantity changes
    modifyItems(
      {
        item,
        quantity: newQuantity,
      },
      {}
    )
      .then(trackSuccessfulQuantityRequest)
      .catch((err) => {
        // On error, revert displayed amount to basket amount
        setAmountSelectedDisplay(item?.quantity ?? 0);
        allowRequest.current = false;
        setAmountRequested({ amount: item?.quantity ?? 0 });
        logAppInfo("Error changing cart item quantity");
        recordAppError(err);
      });
  };

  const handleUpgradeItemGroup = useCallback(async () => {
    const upgradeOption = itemGroupUpgrades?.upgradeOptions?.[0];

    const itemData = upgradeOption?.itemData;
    const { quantity = 1 } = item;

    if (!itemGroupUpgrades?.itemType) {
      recordAppError(new Error("Item type is undefined in upgrade options"));
      return;
    }

    const upgradeConfig = {
      itemData,
      itemType: itemGroupUpgrades.itemType,
      quantity,
    };

    await upgradeItemGroup(item, upgradeConfig, {
      shouldPersist: true,
    });
    notifier.success(t("Your items have been upgraded!", { count: quantity }));
  }, [item, itemGroupUpgrades, upgradeItemGroup, t, recordAppError]);

  const getQuantitySelector = () => {
    if (item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id) {
      return null;
    }
    // if difference between min and max selection amount is greater than threshold, use dropdown
    if (
      !!maxSelectionAmount &&
      maxSelectionAmount - minSelectionAmount >
        DISPLAY_QUANTITY_DROPDOWN_THRESHOLD &&
      !itemGroupsAreSplit
    ) {
      return (
        <CartQuantitySelect
          id={ticketType?.id.toString() || productAttribute?.id.toString()}
          amountSelected={amountSelectedDisplay}
          setAmountSelected={setAmountRequested}
          minSelectionAmount={minSelectionAmount}
          maxSelectionAmount={maxSelectionAmount}
          disabled={disabled}
        />
      );
    }

    return (
      <CartQuantityButtons
        amountSelected={amountSelectedDisplay}
        setAmountSelected={setAmountRequested}
        minSelectionAmount={minSelectionAmount}
        maxSelectionAmount={maxSelectionAmount}
        // Inside CartQuantityButtons, we use the add and remove functions to handle the quantity change
        // for item types that are split across multiple item groups. For all other item types, we use the setAmountSelected function.
        addItem={itemGroupsAreSplit ? add : null}
        removeItem={itemGroupsAreSplit ? remove : null}
        item={item}
        disabled={disabled}
      />
    );
  };

  const ticketPackagesMap = useMemo(() => {
    const itemGroupsCopy = itemGroups.map((ig) => ({ ...ig }));
    let childParentMap: Record<string, TicketItemGroupType[]> = {};

    if (itemGroupsCopy) {
      childParentMap = itemGroupsCopy.reduce((prev, curr) => {
        const prevCopy = prev;
        if (curr.parent) {
          prevCopy[curr.parent] = [...(prev[curr.parent] || []), curr];
        }
        return prevCopy;
      }, childParentMap);
    }

    return childParentMap;
  }, [itemGroups]);

  // close the footer when removing seats from the cart
  const handleCloseCart = useCallback(() => {
    if (onCartClose) {
      onCartClose();
    }
  }, [onCartClose]);

  const getFormattedEvent = useCallback((): SeatingEventPropertyMap => {
    if (membershipGroupData) {
      return {
        ...seatingServiceState.createNewSeatingObject(
          membershipGroupData,
          SEATING_ITEM_TYPE_MAP.MEMBERSHIP.propertyMap
        ),
        no_isolated_seats:
          membershipGroupData.reserved_seating_benefits?.[0].no_isolated_seats,
        assigned_space:
          membershipGroupData.reserved_seating_benefits?.[0].space,
      };
    }

    return seatingServiceState.createNewSeatingObject(
      event ?? eventData,
      SEATING_ITEM_TYPE_MAP.EVENT.propertyMap
    );
  }, [event, eventData, membershipGroupData, seatingServiceState]);

  if (item.parent) return null;
  return (
    <ItemContainer data-testid={`cart-item-${item.id}`}>
      {/* Handles removing isolated, full-purchase, resale seats, or minimum-purchase seats from the cart */}
      {seatsToRemove && (
        <SeatRemoveModal
          isOpen={modalOpen}
          onClose={(err) => {
            rejectModal(err);
            handleCloseCart();
          }}
          resolve={(val) => {
            resolveModal(val);
            handleCloseCart();
          }}
          seatsToRemove={seatsToRemove}
          itemGroupParent={item.parent}
        />
      )}
      {/* Handles removing seats from the cart when not on a seating map page */}
      {removeAllSeatsData && (
        <RemoveAllSeatsModal
          isOpen={modalOpen}
          onClose={(err) => {
            rejectModal(err);
            handleCloseCart();
          }}
          resolve={(val) => {
            resolveModal(val);
            handleCloseCart();
          }}
          eventData={removeAllSeatsData.eventData}
          $data={removeAllSeatsData.$data}
        />
      )}
      <Flex justifyContent="space-between" width="100%">
        <Flex
          justifyContent="space-between"
          width="86%"
          direction={{ base: "column", lg: "row" }}
        >
          <Box width="75%">
            <Text noOfLines={2}>{item.description}</Text>
            {event && (
              <Text fontWeight="normal" data-testid="ticket-starts-date">
                {getFormattedDate({
                  date: getStartsOnDate(event, ticketType),
                  timezone: event.timezone,
                  displayTime: true,
                })}
              </Text>
            )}
          </Box>
          <Box width={67} id="qcontainer">
            {!hasSubItems && !isMembershipRenewal && getQuantitySelector()}
          </Box>
        </Flex>
        <Box>
          <StyledIconButton
            hidden={hasSeat || hasLocation}
            aria-label={t("Delete")}
            iconName="Trash"
            iconColor="gray.500"
            iconSize="24px"
            onClick={deleteItem}
            disabled={disabled}
          />
        </Box>
      </Flex>
      {itemGroupUpgrades?.upgradeOptions?.length > 0 && !item.is_upgraded && (
        <Box fontWeight="normal" marginTop="md">
          <UpgradeOffer
            itemType={itemGroupUpgrades.itemType}
            name={itemGroupUpgrades.upgradeOptions?.[0]?.name ?? ""}
            upgradeAmount={
              itemGroupUpgrades.upgradeOptions?.[0]?.priceDelta ?? 0
            }
            dataTestId={`upgrade-offer-${item.id}`}
            onUpgrade={handleUpgradeItemGroup}
            isLoading={isBasketUpdating}
          />
        </Box>
      )}
      {item.id &&
        ticketPackagesMap[item.id] &&
        ticketPackagesMap[item.id].map((childIg) => {
          return <ChildPackageCartItem key={childIg.id} itemGroup={childIg} />;
        })}

      {/* Assigned seating specific logic. We do two separate maps here as assigned seating (identifier) does not populate the location_permissions array. */}
      {hasSeat &&
        item?.permissions?.map((permission) => {
          if ("seat" in permission) {
            return (
              <Flex
                key={permission.id}
                justifyContent="space-between"
                alignItems="center"
              >
                <AssignedSeatCartItem
                  location_identifier={permission?.seat?.location_identifier}
                  segment_identifier={permission?.seat?.segment_identifier}
                  identifier={permission?.seat?.identifier}
                />
                {!event && !eventData && !membershipGroupData && (
                  <Spinner
                    aria-label={t("Loading")}
                    color="var(--clr-paint-gray-500)"
                  />
                )}
                <StyledIconButton
                  disabled={disabled}
                  hidden={!event && !eventData && !membershipGroupData}
                  aria-label={t("Delete")}
                  iconName="Trash"
                  iconColor="gray.500"
                  iconSize="24px"
                  onClick={async () => {
                    try {
                      await seatingActions.initRemoveSeatPermissions(
                        permission,
                        item,
                        getFormattedEvent()
                      );
                    } catch (error) {
                      recordAppError(error);
                    }
                  }}
                />
              </Flex>
            );
          }

          return null;
        })}

      {hasLocation &&
        item?.location_permissions?.map((permission) => {
          if ("location" in permission) {
            return (
              <LocationCartItem
                key={permission.id}
                item={item}
                permission={permission}
                isDisabled={disabled}
              />
            );
          }

          return null;
        })}
    </ItemContainer>
  );
};

export default CartItem;
